import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, BundleCombinationChangeEvent, BundleVariantUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    document.addEventListener(ThemeEvents.bundleCombinationChange, this.#updateImageGroup.bind(this));
    target?.addEventListener(ThemeEvents.bundleVariantUpdate, this.#handleColorUpdate.bind(this), { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    /**
     * @type {{ combination: number[], media: number[] }[]}
     */
    this.combinationSet = JSON.parse(this.querySelector('[type="application/ld+json"][data-conbinations]')?.textContent || '{}')
    this.currenCombinationSet = this.combinationSet[0]
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {BundleVariantUpdateEvent|null} event - The bundle update event.
   */
  #handleColorUpdate(event){
    this.color = event?.detail.color || document.querySelector('.product__options-block fieldset[data-type="swatch"] input[type="radio"]:checked')?.value;

    if(!this.color) return;

    [this.refs.slideshow.refs.slides, this.refs.slideshow.refs.thumbnails].forEach(
    /** @param {HTMLElement[]} slides */
    (slides) => {
      slides.forEach((slide) => {
          const mediaCombination = JSON.parse(slide.dataset.productCombination || '{}')

          slide.classList.toggle('hidden',
            !(JSON.stringify(mediaCombination) === JSON.stringify(this.currenCombinationSet.combination) && (!slide.hasAttribute('data-media-color') || slide.dataset.mediaColor === this.color)));
      })
    })
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {BundleCombinationChangeEvent} event - The bundle update event.
   */
  #updateImageGroup(event) {
    const currenCombinationSet =  this.combinationSet.find(set =>{
      return JSON.stringify(set.combination) === JSON.stringify(event.detail.productsId)
    })

    if(!currenCombinationSet) return;

    this.currenCombinationSet = currenCombinationSet;

    [this.refs.slideshow.refs.slides, this.refs.slideshow.refs.thumbnails].forEach(
      /** @param {HTMLElement[]} slides */
      (slides) => {
        slides?.forEach((slide) => {
          const mediaCombination = JSON.parse(slide.dataset.productCombination || '{}')

          slide.classList.toggle('hidden', JSON.stringify(mediaCombination) !== JSON.stringify(event.detail.productsId));
        })
    })

    this.#handleColorUpdate(null)
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
